import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User, Mail, Phone, Lock, Save, Edit2, X,
  CheckCircle2, Loader2, MailX, Trash2,
} from 'lucide-react';

export function ProfileSettings() {
  const { user, updateProfile, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    current_password: '',
    new_password: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        phone: user.phone || '',
        current_password: '',
        new_password: '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const updateData = {};
      if (form.full_name !== user.full_name) updateData.full_name = form.full_name;
      if (form.phone !== user.phone) updateData.phone = form.phone;
      if (form.current_password && form.new_password) {
        updateData.current_password = form.current_password;
        updateData.new_password = form.new_password;
      }
      if (Object.keys(updateData).length === 0) {
        setError('No changes to save');
        return;
      }
      await updateProfile(updateData);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setForm((p) => ({ ...p, current_password: '', new_password: '' }));
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-settings">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Profile & Settings</h2>
        <p className="text-sm text-slate-500 mt-1">Manage your account details and credentials</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {success}
        </div>
      )}

      {/* Profile form */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-4 h-4 text-[#00D1B2]" />
            Profile Information
          </h3>
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setForm({
                  full_name: user.full_name || '',
                  phone: user.phone || '',
                  current_password: '',
                  new_password: '',
                });
                setError('');
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <User className="w-3.5 h-3.5" /> Full Name
              </Label>
              <Input
                id="full_name"
                name="full_name"
                autoComplete="name"
                value={form.full_name}
                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
                data-testid="profile-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Mail className="w-3.5 h-3.5" /> Email Address
              </Label>
              <Input
                id="email"
                name="email"
                autoComplete="email"
                value={user?.email || ''}
                disabled
                className="bg-gray-50"
                data-testid="profile-email-input"
              />
              <p className="text-xs text-slate-400">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Phone className="w-3.5 h-3.5" /> WhatsApp Number
              </Label>
              <Input
                id="phone"
                name="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                disabled={!isEditing}
                placeholder="+91 9876543210"
                className={!isEditing ? 'bg-gray-50' : ''}
                data-testid="profile-phone-input"
              />
            </div>
          </div>

          {isEditing && (
            <>
              <hr className="border-gray-100" />
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Change Password
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="current_password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Current Password
                  </Label>
                  <Input
                    id="current_password"
                    name="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={form.current_password}
                    onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))}
                    placeholder="Enter current password"
                    data-testid="profile-current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    New Password
                  </Label>
                  <Input
                    id="new_password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={form.new_password}
                    onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))}
                    placeholder="Enter new password"
                    data-testid="profile-new-password-input"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#00D95F] hover:bg-[#00C054]"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            </>
          )}
        </form>
      </Card>

      {/* Account Status */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Account Status</h3>
        <div className="space-y-3">
          {[
            { label: 'Verified', value: user?.is_verified ? 'Yes' : 'Pending', ok: user?.is_verified },
            { label: 'Facebook', value: user?.fb_connected ? 'Connected' : 'Not connected', ok: user?.fb_connected },
            { label: 'Chatwoot', value: user?.chatwoot_user_id ? 'Active' : 'Setting up…', ok: !!user?.chatwoot_user_id },
          ].map(({ label, value, ok }) => (
            <div key={label} className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{label}</span>
              <span className={`flex items-center gap-1.5 font-medium ${ok ? 'text-green-600' : 'text-amber-600'}`}>
                <CheckCircle2 className="w-4 h-4" />
                {value}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Account Management */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Account Management</h3>
        <div className="space-y-3">
          <Link
            to="/unsubscribe"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 transition-colors"
            data-testid="unsubscribe-link"
          >
            <MailX className="w-4 h-4" />
            Unsubscribe from communications
          </Link>
          <Link
            to="/data-deletion"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors"
            data-testid="data-deletion-link"
          >
            <Trash2 className="w-4 h-4" />
            Request data deletion
          </Link>
        </div>
      </Card>
    </div>
  );
}
